import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { withRetry } from '../utils/utils';
import { config } from '../config';
import axios from 'axios';

export interface Bar {
    "c": number;
    "h": number;
    "l": number;
    "n": number;
    "o": number;
    "t": string;
    "v": number;
    "vw": number;
}

export interface Order {
    id: string;
    client_order_id: string;
    created_at: string;
    updated_at: string;
    submitted_at: string;
    filled_at: string;
    expired_at: string;
    canceled_at: string;
    failed_at: string;
    replaced_at: string;
    replaced_by: string;
    replaces: string;
    asset_id: string;
    symbol: string;
    asset_class: string;
    notional: string;
    qty: string;
    filled_qty: string;
    filled_avg_price: string;
    order_class: string;
    order_type: string;
    type: string;
    side: string;
    position_intent: string;
    time_in_force: string;
    limit_price: string;
    stop_price: string;
    status: string;
    extended_hours: boolean
    legs: string;
    trail_percent: string;
    trail_price: string;
    hwm: string;
    subtag: string;
    source: string;
}

export interface SymbolData {
    bars: Bar[];
    orders: Order[];
}

export interface SymbolState {
    symbols: Record<string, SymbolData>;
    loading: boolean;
    error: string | null;
}

// Initial state
const initialState: SymbolState = {
    symbols: {},
    loading: false,
    error: null,
}

export interface SymbolBarsParams {
    symbol: string;
    period: string; // 1D, 1W, 1M, 6M
    timeframe: string; // 5Min, 1H, 1D, 1D, 1D, 1D
    startDate: string;
    endDate: string;
}

export interface SymbolOrdersParams {
    status: 'all' | 'open' | 'closed';
    limit: number;
    symbol: string;
}

export const fetchBars = createAsyncThunk<{bars: Bar[]}, SymbolBarsParams, { rejectValue: string }>(
    'symbols/fetchBars',
    async (params, thunkAPI) => {
        try {
            const history = await withRetry(async () => {
                const result = await axios({
                    baseURL: config.alpaca.dataUrl,
                    method: 'get',
                    url: `v2/stocks/${params.symbol}/bars?timeframe=${params.timeframe}&limit=1000&adjustment=raw&feed=iex&sort=asc&start=${params.startDate}&end=${params.endDate}`,
                    headers: {
                        'APCA-API-KEY-ID': config.alpaca.keyId,
                        'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                        'accept': 'application/json'
                    }
                  });
                return result.data;
            }, 2);

            return history;
        } catch (error) {
            return thunkAPI.rejectWithValue('Network error occurred');
        }
    }
);


export const fetchOrders = createAsyncThunk<Order[], SymbolOrdersParams, { rejectValue: string }>(
    'symbols/fetchOrders',
    async (params, thunkAPI) => {
        try {
            const history = await withRetry(async () => {
                const result = await axios({
                    baseURL: config.alpaca.baseUrl,
                    method: 'get',
                    url: `v2/orders?status=${params.status}&limit=${params.limit}&symbols=${params.symbol}`,
                    headers: {
                        'APCA-API-KEY-ID': config.alpaca.keyId,
                        'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                        'accept': 'application/json'
                    }
                  });
                return result.data;
            }, 2);

            return history;
        } catch (error) {
            return thunkAPI.rejectWithValue('Network error occurred');
        }
    }
);

export const symbolSlice = createSlice({
    name: 'symbol',
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBars.fulfilled, (state, action) => {
            state.loading = false;
            if (!state.symbols[action.meta.arg.symbol]) {
                state.symbols[action.meta.arg.symbol] = { bars: [], orders: [] };
            }
            if (action.payload.bars?.length) {
                state.symbols[action.meta.arg.symbol].bars = action.payload.bars;
            }
        });
        builder.addCase(fetchBars.pending, (state, action) => {
            const symbol = action.meta.arg.symbol;
            if (!state.symbols[symbol]?.bars?.length) {
                state.loading = true;
            }
            state.error = null;
        });
        builder.addCase(fetchOrders.fulfilled, (state, action) => {
            state.loading = false;
            if (!state.symbols[action.meta.arg.symbol]) {
                state.symbols[action.meta.arg.symbol] = { bars: [], orders: [] };
            }
            state.symbols[action.meta.arg.symbol].orders = action.payload;
        });
        builder.addCase(fetchOrders.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addMatcher(isAnyOf(fetchBars.rejected, fetchOrders.rejected), (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Something went wrong';
        });
    },
});
